import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { ordersApi, useGetDispatchOrdersQuery } from "../redux/services/orders";
import { AppDispatch, useAppSelector } from "../redux/services/store";
import { selectUser } from "../redux/slices/authSlice";
import {
  AlarmReason,
  isAudioUnlocked,
  isSoundEnabled,
  ringOrderAlarmFor,
  startOrderAlarm,
  stopOrderAlarm,
  subscribeAudioState,
  subscribeOrderAlarm,
  subscribeSoundEnabled,
  unlockAudio,
} from "../utils/notificationSound";

const LOCKED_TOAST_ID = "dispatch-alarm-locked";
const ONE_MINUTE_MS = 60_000;
// Orders waiting on the company ring in cycles: one minute on, one minute off.
const WAITING_RING_MS = 60_000;
const WAITING_PAUSE_MS = 60_000;

const formatNaira = (value?: string | number | null) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0 }).format(Number(value ?? 0));

// Each order's acceptance / payment alert plays once, even across refreshes.
const ALERTED_KEY = "fleetrify_dispatch_alerted";

const alreadyAlerted = (key: string) => {
  try {
    return (JSON.parse(localStorage.getItem(ALERTED_KEY) ?? "[]") as string[]).includes(key);
  } catch {
    return false;
  }
};

const markAlerted = (key: string) => {
  try {
    const keys = JSON.parse(localStorage.getItem(ALERTED_KEY) ?? "[]") as string[];
    localStorage.setItem(ALERTED_KEY, JSON.stringify([...keys, key].slice(-300)));
  } catch {
    // Storage blocked: worst case the same alert can play again after a refresh.
  }
};

// Statuses where a fresh "paid" can actually happen: right after acceptance
// (pay before delivery) or at delivery (pay after delivery). Later driver
// updates on an order paid long ago must not ring.
const PAYABLE_STATUSES = new Set(["accepted", "delivered"]);

/**
 * Called for every realtime "order.status.updated" event. The event doesn't
 * say who did what, so this reads the day-to-day order itself:
 * - accepted, with the company's offer as the latest one → the customer took
 *   the company's counter: one-minute alarm.
 * - payment_status turned "paid" → one-minute "money in" chime.
 * A company accept (pending, or the customer's own counter) stays quiet.
 */
export const handleDispatchOrderEvent = async (
  dispatch: AppDispatch,
  orderId: string,
  event: { status?: string; payment_status?: string; order_type?: string },
  onView: () => void
) => {
  // Only day-to-day orders negotiate; skip the lookup (and its 404) for the rest.
  if (event.order_type && event.order_type !== "local_dispatch") return;
  // Nothing to check once the order is closed.
  if (event.status === "rejected" || event.status === "cancelled") return;
  // If the event already says it isn't paid and isn't an acceptance, skip the lookup.
  if (event.status !== "accepted" && event.payment_status && event.payment_status !== "paid") return;

  let raw: any;
  try {
    const res = await dispatch(
      ordersApi.endpoints.getDispatchOrderById.initiate({ id: orderId }, { forceRefetch: true })
    ).unwrap();
    raw = res?.data?.data?.order ?? res?.data?.data ?? res?.data?.order ?? res?.order;
  } catch {
    return; // Not a day-to-day order (404) or the lookup failed.
  }
  if (!raw?.status) return;
  const label = raw.order_number ? `Order ${raw.order_number}` : undefined;

  const acceptedKey = `accepted:${orderId}`;
  // Once it's paid the acceptance is old news (this browser just missed it).
  if (
    raw.status === "accepted" &&
    raw.counter_offer?.proposed_by === "company" &&
    raw.payment_status !== "paid" &&
    !alreadyAlerted(acceptedKey)
  ) {
    markAlerted(acceptedKey);
    ringOrderAlarmFor("customer-accepted", ONE_MINUTE_MS);
    toast.success(
      `Customer accepted your offer of ${formatNaira(raw.counter_offer.company_offered_price ?? raw.price)}`,
      { description: label, duration: ONE_MINUTE_MS, action: { label: "View", onClick: onView } }
    );
  }

  const paidKey = `paid:${orderId}`;
  if (raw.payment_status === "paid" && PAYABLE_STATUSES.has(raw.status) && !alreadyAlerted(paidKey)) {
    markAlerted(paidKey);
    ringOrderAlarmFor("customer-paid", ONE_MINUTE_MS);
    toast.success(`Payment received: ${formatNaira(raw.price)}`, {
      description: label,
      duration: ONE_MINUTE_MS,
      action: { label: "View", onClick: onView },
    });
  }
};

/**
 * While any day-to-day order is waiting on the company (pending or
 * customer_countered), the alarm rings for a minute, pauses for a minute, and
 * repeats. Accepting, countering or rejecting clears it from those counts and
 * the cycle stops. A new order or a new customer counter (the count going up)
 * starts ringing straight away, even in the middle of a pause.
 *
 * Counts refresh instantly on the realtime order events (they invalidate
 * "Orders") and every 30s as a fallback in case the socket drops.
 */
export const useDispatchOrderAlarm = () => {
  const isAuthenticated = !!Cookies.get("token");
  const user = useAppSelector(selectUser);
  const isCompanyUser = !!user?.company?.id;

  const { data } = useGetDispatchOrdersQuery(
    { per_page: 1 },
    { skip: !isAuthenticated || !isCompanyUser, pollingInterval: 30000 }
  );
  const counts = data?.data?.counts;
  const waiting = isAuthenticated ? (counts?.pending ?? 0) + (counts?.customer_countered ?? 0) : 0;
  const shouldRing = waiting > 0;

  const [audioUnlocked, setAudioUnlocked] = useState(isAudioUnlocked());
  useEffect(() => subscribeAudioState(setAudioUnlocked), []);

  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  useEffect(() => subscribeSoundEnabled(setSoundOn), []);

  const [reasons, setReasons] = useState<ReadonlySet<AlarmReason>>(new Set());
  useEffect(() => subscribeOrderAlarm(setReasons), []);

  // Bumped whenever something new is waiting, to restart the cycle at "ringing".
  const [cycleKey, setCycleKey] = useState(0);
  const previousWaiting = useRef(0);
  useEffect(() => {
    if (waiting > previousWaiting.current) setCycleKey((key) => key + 1);
    previousWaiting.current = waiting;
  }, [waiting]);

  useEffect(() => {
    if (!shouldRing) {
      stopOrderAlarm("awaiting-company");
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const ring = () => {
      startOrderAlarm("awaiting-company");
      timer = setTimeout(pause, WAITING_RING_MS);
    };
    const pause = () => {
      stopOrderAlarm("awaiting-company");
      timer = setTimeout(ring, WAITING_PAUSE_MS);
    };
    ring();
    // Only cancel the timer: stopping here and starting again on a restart
    // would play two rounds on top of each other. If nothing is waiting any
    // more, the branch above stops it; unmount/logout stop it below.
    return () => clearTimeout(timer);
  }, [shouldRing, cycleKey]);

  // Logging out silences everything, including a one-minute ring in progress.
  useEffect(() => {
    if (!isAuthenticated) stopOrderAlarm();
  }, [isAuthenticated]);

  useEffect(() => () => stopOrderAlarm(), []);

  // Browsers mute every page until the user clicks on it once. Say so,
  // instead of leaving staff to wonder why there's no sound.
  const customerAccepted = reasons.has("customer-accepted");
  const customerPaid = reasons.has("customer-paid");
  useEffect(() => {
    // No point asking to unlock sound that has been switched off in settings.
    if ((shouldRing || customerAccepted || customerPaid) && !audioUnlocked && soundOn) {
      toast.warning(
        shouldRing
          ? `${waiting} day-to-day order${waiting === 1 ? " is" : "s are"} waiting for your response`
          : customerPaid
          ? "A customer made a payment"
          : "A customer accepted your offer",
        {
          id: LOCKED_TOAST_ID,
          duration: Infinity,
          description: "Your browser keeps the alert sound off until you click on the page.",
          action: { label: "Turn on sound", onClick: () => { unlockAudio(); } },
        }
      );
    } else {
      toast.dismiss(LOCKED_TOAST_ID);
    }
  }, [shouldRing, customerAccepted, customerPaid, audioUnlocked, soundOn, waiting]);
};
